import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Inbox,
  ListChecks,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/design-system/primitives";
import {
  useClinicianQueue,
  useClinicianWorklist,
} from "@/lib/api/clinicianHooks";
import type { QueueItem } from "@/types/clinician";

function name(item: QueueItem) {
  return item.patient.display_name || item.patient.email || "Unknown patient";
}

function wait(hours: number) {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 48) return `${Math.floor(hours)}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function OverviewScreen() {
  const queue = useClinicianQueue(true);
  const worklist = useClinicianWorklist();
  const items = queue.data?.items ?? [];
  const needsReply = items.filter(
    (item) =>
      item.needs_clinician &&
      ["new", "ready", "in_progress"].includes(item.workflow_state),
  );
  const urgent = needsReply.filter((item) => item.urgency === 3);
  const overdue = needsReply.filter((item) => item.sla_breached);
  const activePrescriptions = (worklist.data?.prescriptions ?? []).filter(
    (item) => item.stage !== "prescribed",
  );
  const attentionMessages = [...needsReply].sort(
    (a, b) =>
      Number(b.urgency === 3) - Number(a.urgency === 3) ||
      Number(b.sla_breached) - Number(a.sla_breached) ||
      b.priority_score - a.priority_score,
  );
  const loading = queue.isLoading || worklist.isLoading;

  return (
    <div className="max-w-[120rem] mx-auto px-14 md:px-30 xl:px-40 py-24 md:py-40 flex flex-col gap-22">
      <header className="flex flex-wrap items-end justify-between gap-12">
        <div className="flex flex-col gap-5">
          <span className="type-label-sm text-ink-muted">
            CLINICIAN OVERVIEW
          </span>
          <h1 className="type-heading-xl">Today at a glance</h1>
          <p className="type-body-sm text-ink-muted max-w-[64rem]">
            The highest-signal work from patient messages, prescriptions,
            appointments, and Healthie tasks—linked directly to the action.
          </p>
        </div>
        <div className="flex items-center gap-10">
          <span className="type-label-xs text-success inline-flex items-center gap-5">
            <span className="w-6 h-6 rounded-full bg-success" aria-hidden />{" "}
            Live via Healthie webhooks
          </span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={queue.isFetching || worklist.isFetching}
            onClick={() => {
              queue.refetch();
              worklist.refetch();
            }}
          >
            <RefreshCw size={12} aria-hidden /> Refresh
          </Button>
        </div>
      </header>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 border border-line-subtle bg-raised divide-x divide-y xl:divide-y-0 divide-line-subtle">
        <Metric
          to="/clinician-portal"
          label="Needs reply"
          value={needsReply.length}
          icon={<Inbox size={15} />}
        />
        <Metric
          to="/clinician-portal"
          label="Safety / overdue"
          value={
            new Set([...urgent, ...overdue].map((item) => item.conversation_id))
              .size
          }
          icon={<AlertTriangle size={15} />}
          tone="danger"
        />
        <Metric
          to="/clinician-portal/today"
          label="Ready to prescribe"
          value={worklist.data?.counts.ready_to_prescribe ?? 0}
          icon={<ListChecks size={15} />}
          tone="success"
        />
        <Metric
          to="/clinician-portal/today?view=visits"
          label="Upcoming visits"
          value={worklist.data?.counts.visits ?? 0}
          icon={<CalendarClock size={15} />}
        />
      </div>

      {loading ? (
        <div className="grid xl:grid-cols-2 gap-14 animate-pulse">
          <div className="h-[28rem] bg-sunken" />
          <div className="h-[28rem] bg-sunken" />
        </div>
      ) : (
        <div className="grid xl:grid-cols-2 gap-14 items-start">
          <OverviewList
            title="Messages needing attention"
            count={attentionMessages.length}
            empty="No patient messages currently need a clinician."
            allTo="/clinician-portal"
          >
            {attentionMessages.slice(0, 8).map((item) => (
              <Link
                key={item.conversation_id}
                to={`/clinician-portal/inbox/${item.conversation_id}`}
                className="p-11 flex items-start justify-between gap-10 hover:bg-sunken focus-visible:outline-none focus-visible:shadow-focus"
              >
                <span className="min-w-0">
                  <span className="type-body-sm font-medium flex items-center gap-6">
                    {name(item)}
                    {item.urgency === 3 && (
                      <span className="type-label-xs text-danger">SAFETY</span>
                    )}
                    {item.sla_breached && (
                      <span className="type-label-xs text-warning">
                        OVERDUE
                      </span>
                    )}
                  </span>
                  <span className="text-11 text-ink-muted line-clamp-2 mt-2">
                    {item.last_patient_message_preview ||
                      item.urgency_reason ||
                      item.category ||
                      "Patient message"}
                  </span>
                </span>
                <span className="type-label-xs text-ink-muted shrink-0 flex items-center gap-4">
                  <Clock3 size={11} /> {wait(item.waiting_hours)}
                </span>
              </Link>
            ))}
          </OverviewList>

          <OverviewList
            title="Clinical worklist"
            count={activePrescriptions.length}
            empty="No prescription work is currently waiting."
            allTo="/clinician-portal/today"
          >
            {activePrescriptions.slice(0, 8).map((item) => (
              <Link
                key={item.id}
                to={`/clinician-portal/today/${item.id}`}
                className="p-11 flex items-start justify-between gap-10 hover:bg-sunken focus-visible:outline-none focus-visible:shadow-focus"
              >
                <span className="min-w-0">
                  <span className="type-body-sm font-medium block truncate">
                    {item.patient.display_name || item.patient.email}
                  </span>
                  <span className="text-11 text-ink-muted block truncate mt-2">
                    {item.product} · {item.stage.replaceAll("_", " ")}
                  </span>
                </span>
                <span className="type-label-xs text-ink-muted shrink-0">
                  {wait(item.waiting_hours)}
                </span>
              </Link>
            ))}
          </OverviewList>

          <OverviewList
            title="Upcoming visits"
            count={worklist.data?.appointments.length ?? 0}
            empty="No upcoming appointments."
            allTo="/clinician-portal/today?view=visits"
          >
            {(worklist.data?.appointments ?? [])
              .slice(0, 5)
              .map((appointment) => (
                <Link
                  key={appointment.id}
                  to="/clinician-portal/today?view=visits"
                  className="p-11 flex items-start justify-between gap-10 hover:bg-sunken"
                >
                  <span>
                    <span className="type-body-sm font-medium block">
                      {appointment.patient.display_name ||
                        appointment.patient.email}
                    </span>
                    <span className="text-11 text-ink-muted">
                      {appointment.appointment_type || "Clinical visit"}
                    </span>
                  </span>
                  <span className="text-11 text-ink-muted shrink-0">
                    {new Date(appointment.starts_at).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </Link>
              ))}
          </OverviewList>

          <OverviewList
            title="Other Healthie tasks"
            count={worklist.data?.tasks.length ?? 0}
            empty="No other Healthie tasks are waiting."
            allTo="/clinician-portal/today?view=tasks"
          >
            {(worklist.data?.tasks ?? []).slice(0, 5).map((task) => (
              <Link
                key={task.id}
                to="/clinician-portal/today?view=tasks"
                className="p-11 flex items-start justify-between gap-10 hover:bg-sunken"
              >
                <span className="min-w-0">
                  <span className="type-body-sm font-medium block truncate">
                    {task.patient.display_name || task.patient.email}
                  </span>
                  <span className="text-11 text-ink-muted line-clamp-2">
                    {task.content}
                  </span>
                </span>
                {task.overdue && (
                  <span className="type-label-xs text-danger shrink-0">
                    OVERDUE
                  </span>
                )}
              </Link>
            ))}
          </OverviewList>
        </div>
      )}
    </div>
  );
}

function Metric({
  to,
  label,
  value,
  icon,
  tone,
}: {
  to: string;
  label: string;
  value: number;
  icon: React.ReactNode;
  tone?: "danger" | "success";
}) {
  return (
    <Link to={to} className="p-14 flex flex-col gap-8 hover:bg-sunken group">
      <span className="flex items-center justify-between text-ink-muted">
        <span className="type-label-xs">{label}</span>
        {icon}
      </span>
      <span
        className={`type-heading-xl tabular-nums ${tone === "danger" ? "text-danger" : tone === "success" ? "text-success" : ""}`}
      >
        {value}
      </span>
      <span className="type-label-xs text-ink-muted flex items-center gap-4 group-hover:text-ink">
        Open queue <ArrowRight size={11} />
      </span>
    </Link>
  );
}

function OverviewList({
  title,
  count,
  empty,
  allTo,
  children,
}: {
  title: string;
  count: number;
  empty: string;
  allTo: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border border-line-subtle bg-raised">
      <div className="px-12 py-10 bg-sunken border-b border-line-subtle flex items-center justify-between gap-8">
        <h2 className="type-label-sm font-medium">
          {title} <span className="text-ink-muted ml-4">{count}</span>
        </h2>
        <Link
          to={allTo}
          className="type-label-xs text-ink-muted hover:text-ink"
        >
          View all <ArrowRight size={11} className="inline" />
        </Link>
      </div>
      {count ? (
        <div className="divide-y divide-line-subtle">{children}</div>
      ) : (
        <div className="p-18 flex items-center gap-8 type-body-sm text-ink-muted">
          <CheckCircle2 size={15} className="text-success" /> {empty}
        </div>
      )}
    </section>
  );
}
